import * as React from "react";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { MessageSquare, Sparkles, Share2, Plus, Copy, Check, ExternalLink } from "lucide-react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";

import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button, Combobox } from "@/components/ui";
import { FormServices, FormRequest } from "@/services/bedrock";
import { kFormatter, useSocialCoachSession } from "@/utils";
import { ShareFormLinkDialog } from "@/components/ShareFormLinkDialog";
import { FormResponsesListDialog } from "@/containers/Coach/Reputation/Forms/components/FormResponsesListDialog";

export function FormLinksTable() {
  const { playerId } = useSocialCoachSession();
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | undefined>(undefined);
  const [initialFormId, setInitialFormId] = useState<string | number | undefined>(undefined);
  const [initialShowCustom, setInitialShowCustom] = useState(false);

  // Filter & Copy States
  const [selectedFormFilter, setSelectedFormFilter] = useState<{ id: string; label: string } | null>(null);
  const [copiedReqId, setCopiedReqId] = useState<number | null>(null);

  // Responses Dialog State
  const [isResponsesOpen, setIsResponsesOpen] = useState(false);
  const [selectedFormForResponses, setSelectedFormForResponses] = useState<any | null>(null);
  const [selectedRequestIdForResponses, setSelectedRequestIdForResponses] = useState<number | undefined>(undefined);

  // 1. Fetch available forms to filter by
  const { data: formsData } = useQuery({
    queryKey: ["forms", { page: 1, perPage: 100 }],
    queryFn: () => FormServices.getForms({ page: 1, perPage: 100, active: true }),
  });
  const formsList = formsData?.content || [];
  const formSelectItems = useMemo(() => {
    return formsList.map(form => ({
      id: String(form.id),
      label: form.displayName,
    }));
  }, [formsList]);

  // 2. Fetch Form Requests filtered by player and formId
  const { data, isLoading } = useQuery({
    queryKey: ["playerFormRequests", playerId, pagination, selectedFormFilter?.id],
    queryFn: () =>
      FormServices.getFormRequests({
        playerId,
        formId: selectedFormFilter ? Number(selectedFormFilter.id) : undefined,
        page: pagination.page,
        perPage: pagination.perPage,
      }),
    enabled: !!playerId,
  });

  const columns = useMemo<ColumnDef<FormRequest>[]>(
    () => [
      {
        id: "formName",
        header: "Form Name",
        cell: ({ row }) => {
          const req = row.original;
          return (
            <span className="font-bold text-slate-800 text-sm font-sans select-none text-left">
              {(req as any).formDisplayName || req.form?.displayName || "Standard Review Form"}
            </span>
          );
        },
      },
      {
        id: "recipient",
        header: "Created For",
        cell: ({ row }) => {
          const req = row.original;
          if (req.fan) {
            return (
              <div className="flex flex-col gap-0.5 font-sans py-1 select-none text-left">
                <div className="flex items-center gap-1.5">
                  <span className="font-bold text-slate-700 text-xs">{req.fan.displayName}</span>
                  {req.useAiPersonalization && <Sparkles className="size-3.5 text-indigo-500 shrink-0 animate-pulse" />}
                </div>
                {req.fan.email && (
                  <span className="text-[10px] text-slate-400 font-medium leading-none mt-0.5">{req.fan.email}</span>
                )}
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-0.5 select-none py-1 text-left">
              <div className="flex items-center gap-1.5">
                <span className="text-slate-500 italic text-xs font-sans block">
                  {req.internalIdentifier || "General (Standard Link)"}
                </span>
                {req.useAiPersonalization && <Sparkles className="size-3.5 text-indigo-500 shrink-0 animate-pulse" />}
              </div>
            </div>
          );
        },
      },
      {
        id: "createdAt",
        header: "Created Date",
        cell: ({ row }) => {
          const req = row.original;
          let dateStr = "-";
          if (req.createdAt) {
            try {
              dateStr = new Date(req.createdAt).toLocaleDateString();
            } catch {}
          }
          return <span className="text-slate-500 text-xs font-medium font-sans select-none">{dateStr}</span>;
        },
      },
      {
        id: "responses",
        header: "Responses",
        cell: ({ row }) => {
          const req = row.original;
          const count = req.totalResponseCount || 0;
          return (
            <Badge
              variant="outline"
              className="bg-emerald-50/50 border-emerald-100 text-emerald-700 font-bold py-0.5 px-2 text-xs select-none font-sans cursor-pointer hover:bg-emerald-100/60 hover:text-emerald-850 hover:border-emerald-200/80 transition-colors h-6 whitespace-nowrap"
              onClick={e => {
                e.stopPropagation();
                setSelectedFormForResponses(req.form || null);
                setSelectedRequestIdForResponses(req.id);
                setIsResponsesOpen(true);
              }}
            >
              {kFormatter(count)} {count === 1 ? "response" : "responses"}
            </Badge>
          );
        },
      },
      {
        id: "urlLink",
        header: "Review Link",
        cell: ({ row }) => {
          const req = row.original;
          const isCopied = copiedReqId === req.id;
          return (
            <div className="flex items-center gap-1 min-w-0" onClick={e => e.stopPropagation()}>
              <span className="text-xs text-slate-500 truncate max-w-[260px] font-mono select-all mr-1">{req.url}</span>
              <Button
                variant="ghost"
                size="icon-sm"
                className="h-7 w-7 text-slate-400 hover:text-slate-700 shrink-0"
                onClick={() => {
                  copy(req.url);
                  toast.success("Link copied!");
                  setCopiedReqId(req.id);
                  setTimeout(() => setCopiedReqId(null), 2000);
                }}
                title="Copy Link"
              >
                {isCopied ? (
                  <Check className="size-3.5 text-emerald-600 animate-bounce" />
                ) : (
                  <Copy className="size-3.5" />
                )}
              </Button>
              <a
                href={req.url}
                target="_blank"
                rel="noopener noreferrer"
                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-muted text-slate-400 hover:text-slate-700 shrink-0 transition-colors"
                title="Open Link in New Tab"
              >
                <ExternalLink className="size-3.5" />
              </a>
            </div>
          );
        },
      },
      {
        id: "link",
        header: () => <div className="text-right pr-4 select-none">Action</div>,
        cell: ({ row }) => {
          const req = row.original;
          return (
            <div className="flex items-center justify-end" onClick={e => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs font-bold px-3.5 flex items-center gap-1.5 rounded-xl border-slate-200 hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer shrink-0 shadow-3xs"
                onClick={() => {
                  setSelectedRequestId(req.id);
                  setInitialFormId(req.form?.id);
                  setIsShareOpen(true);
                }}
              >
                <Share2 className="size-3.5 shrink-0 text-slate-500" />
                Share Link
              </Button>
            </div>
          );
        },
      },
    ],
    [copiedReqId]
  );

  const handleCreateNewLinkClick = () => {
    setSelectedRequestId(undefined);
    setInitialFormId(undefined);
    setInitialShowCustom(true);
    setIsShareOpen(true);
  };

  const isEmpty = !isLoading && data?.content.length === 0 && !selectedFormFilter;

  if (isEmpty) {
    return <FormLinksEmptyState onCreateClick={handleCreateNewLinkClick} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Top Filters & Actions Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none mb-1 w-full">
        {/* Top Left: Form Filter Combobox */}
        <div className="w-full sm:w-64 shrink-0">
          <Combobox
            items={formSelectItems}
            value={selectedFormFilter}
            onValueChange={val => {
              setSelectedFormFilter(val);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            showClear
            placeholder="Filter by Form..."
            className="w-full bg-white text-xs h-9"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.content ?? []}
        loading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={data?.pagination?.totalPages}
        rowCount={data?.pagination?.totalCount}
      />

      {/* Reusable Share Form Link Dialog */}
      <ShareFormLinkDialog
        open={isShareOpen}
        onOpenChange={open => {
          setIsShareOpen(open);
          if (!open) {
            setSelectedRequestId(undefined);
            setInitialFormId(undefined);
            setInitialShowCustom(false);
          }
        }}
        playerId={playerId!}
        formRequestId={selectedRequestId}
        initialShowCustom={initialShowCustom}
        initialFormId={initialFormId}
      />

      {/* Reusable Form Response Viewer Dialog */}
      {isResponsesOpen && (
        <FormResponsesListDialog
          open={isResponsesOpen}
          onOpenChange={open => {
            setIsResponsesOpen(open);
            if (!open) {
              setSelectedFormForResponses(null);
              setSelectedRequestIdForResponses(undefined);
            }
          }}
          form={selectedFormForResponses}
          formRequestId={selectedRequestIdForResponses}
        />
      )}
    </div>
  );
}

function FormLinksEmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-12 text-center bg-card shadow-sm mt-8 select-none animate-in fade-in duration-200">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6 text-primary">
        <MessageSquare className="h-10 w-10" />
      </div>
      <h3 className="text-2xl font-bold tracking-tight mb-2">No Form Links Generated Yet</h3>
      <p className="text-muted-foreground max-w-md mx-auto mb-8">
        Once you customize a review link or create an AI-personalized link for a specific client, your active links and
        their response tallies will appear here!
      </p>
      <Button size="lg" onClick={onCreateClick}>
        <Plus className="mr-2 h-5 w-5" />
        Personalize your first link
      </Button>
    </div>
  );
}
